import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { frameTc, parseFrameTc, stepFrames, type MamSubclip } from '@airpost/shared';
import { ElementLevels } from '../element-levels.js';
import { t } from '../i18n.js';
import { VuMeter } from '../vu-meter.js';

/**
 * The MAM's preview player, after Nebula's: the proxy (or the sound file) on one media element, timecode with
 * frames, a trackbar with the in and out marks and the subclips, the transport (to the start, to the in mark,
 * five frames, one frame, play, …, to the out mark, to the end), J/K/L shuttle, a loop between the marks, and a
 * stereo VU meter read from the element through Web Audio. One element serves every asset, so its audio graph is
 * built once.
 */
export interface PlayerSource {
  key: string;
  url: string;
  media: 'video' | 'audio';
  title: string;
  durationMs: number | null;
  poster?: string | null;
  /** A waveform picture of a sound, shown in place of a picture. */
  wave?: string | null;
}
export interface PlayerMarks {
  inMs: number | null;
  outMs: number | null;
}
export interface MamPlayerHandle {
  togglePlay(): void;
  /** Play from the in mark to the out mark (or the given range) and stop there. */
  playRange(inMs?: number | null, outMs?: number | null): void;
  seek(ms: number): void;
  /** A key the player answers (J K L I O, arrows, Home, End, space); true when it did. */
  handleKey(e: KeyboardEvent | React.KeyboardEvent): boolean;
}
interface Props {
  source: PlayerSource | null;
  marks: PlayerMarks;
  onMarks(m: PlayerMarks): void;
  subclips?: MamSubclip[];
  /** Extra buttons under the transport (add the range to the basket, save it as a subclip). */
  actions?: React.ReactNode;
}

const SHUTTLE = [-8, -4, -2, -1, 0, 1, 2, 4, 8];
/** Where an element stands, in milliseconds. */
const msOf = (v: HTMLVideoElement | null) => Math.round((v?.currentTime ?? 0) * 1000);

export const MamPlayer = forwardRef<MamPlayerHandle, Props>(function MamPlayer({ source, marks, onMarks, subclips = [], actions }, ref) {
  const video = useRef<HTMLVideoElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const [levels] = useState(() => new ElementLevels());
  const stopAt = useRef<number | null>(null);
  const shuttle = useRef(0);
  const reverse = useRef<number | null>(null);
  const [tMs, setTMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(0);
  const [loop, setLoop] = useState(false);
  const [muted, setMuted] = useState(false);
  const [metaMs, setMetaMs] = useState<{ key: string; ms: number } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [inText, setInText] = useState<string | null>(null);
  const [outText, setOutText] = useState<string | null>(null);
  const durationMs = (metaMs && metaMs.key === source?.key ? metaMs.ms : null) ?? source?.durationMs ?? null;
  const error = failed && failed === source?.key;

  const el = () => video.current;

  /** The meter's audio, woken on a play (a user's gesture lets the device start). */
  const ensureGraph = useCallback(() => levels.wake(el()), [levels]);

  const stopReverse = () => {
    if (reverse.current !== null) window.clearInterval(reverse.current);
    reverse.current = null;
  };
  const setShuttle = useCallback((r: number) => {
    const v = el();
    if (!v) return;
    stopReverse();
    shuttle.current = r;
    setRate(r);
    if (r > 0) {
      ensureGraph();
      v.playbackRate = r;
      void v.play().catch(() => undefined);
    } else {
      v.pause();
      v.playbackRate = 1;
      if (r < 0) {
        // the element cannot play backwards: step back in time, |r| seconds per second
        const startedAt = performance.now();
        const from = v.currentTime;
        reverse.current = window.setInterval(() => {
          const s = Math.max(0, from + (r * (performance.now() - startedAt)) / 1000);
          v.currentTime = s;
          setTMs(Math.round(s * 1000));
          if (s <= 0) setShuttleRef.current(0);
        }, 80);
      }
    }
  }, [ensureGraph]);
  const setShuttleRef = useRef(setShuttle);
  useEffect(() => {
    setShuttleRef.current = setShuttle;
  }, [setShuttle]);

  const seek = useCallback((ms: number) => {
    const v = el();
    if (!v) return;
    const end = durationMs ?? Number.POSITIVE_INFINITY;
    const s = Math.min(Math.max(0, ms), end) / 1000;
    v.currentTime = s;
    setTMs(Math.round(s * 1000));
  }, [durationMs]);

  const togglePlay = useCallback(() => {
    const v = el();
    if (!v || !source) return;
    stopReverse();
    stopAt.current = null;
    if (v.paused) {
      ensureGraph();
      v.playbackRate = 1;
      shuttle.current = 1;
      setRate(1);
      if (durationMs !== null && msOf(video.current) >= durationMs - 40) v.currentTime = (marks.inMs ?? 0) / 1000;
      void v.play().catch(() => undefined);
    } else {
      v.pause();
      shuttle.current = 0;
      setRate(0);
    }
  }, [source, ensureGraph, durationMs, marks.inMs]);

  const playRange = useCallback((inMs?: number | null, outMs?: number | null) => {
    const v = el();
    if (!v || !source) return;
    stopReverse();
    const a = inMs !== undefined ? inMs : marks.inMs;
    const b = outMs !== undefined ? outMs : marks.outMs;
    v.currentTime = (a ?? 0) / 1000;
    stopAt.current = b ?? null;
    ensureGraph();
    v.playbackRate = 1;
    shuttle.current = 1;
    setRate(1);
    void v.play().catch(() => undefined);
  }, [source, marks.inMs, marks.outMs, ensureGraph]);

  const markIn = useCallback(() => {
    const at = msOf(video.current);
    onMarks({ inMs: at, outMs: marks.outMs !== null && marks.outMs <= at ? null : marks.outMs });
  }, [marks.outMs, onMarks]);
  const markOut = useCallback(() => {
    const at = msOf(video.current);
    onMarks({ inMs: marks.inMs !== null && marks.inMs >= at ? null : marks.inMs, outMs: at });
  }, [marks.inMs, onMarks]);
  const step = useCallback((frames: number) => {
    const v = el();
    if (!v) return;
    v.pause();
    stopReverse();
    shuttle.current = 0;
    setRate(0);
    seek(stepFrames(msOf(video.current), frames, durationMs));
  }, [seek, durationMs]);

  const handleKey = useCallback(
    (e: KeyboardEvent | React.KeyboardEvent): boolean => {
      if (!source) return false;
      const k = e.key;
      if (k === ' ' || k === 'k' || k === 'K' || k === 'κ' || k === 'Κ') {
        if (k === ' ') togglePlay();
        else setShuttle(0);
      } else if (k === 'l' || k === 'L' || k === 'λ' || k === 'Λ') {
        const cur = shuttle.current;
        setShuttle(SHUTTLE[Math.min(SHUTTLE.length - 1, SHUTTLE.indexOf(cur <= 0 ? 0 : cur) + 1)]!);
      } else if (k === 'j' || k === 'J' || k === 'ξ' || k === 'Ξ') {
        const cur = shuttle.current;
        setShuttle(SHUTTLE[Math.max(0, SHUTTLE.indexOf(cur >= 0 ? 0 : cur) - 1)]!);
      } else if (k === 'i' || k === 'I' || k === 'ι' || k === 'Ι') {
        if (e.altKey) onMarks({ ...marks, inMs: null });
        else markIn();
      } else if (k === 'o' || k === 'O' || k === 'ο' || k === 'Ο') {
        if (e.altKey) onMarks({ ...marks, outMs: null });
        else markOut();
      } else if (k === 'ArrowLeft') step(e.shiftKey ? -5 : -1);
      else if (k === 'ArrowRight') step(e.shiftKey ? 5 : 1);
      else if (k === 'Home') seek(e.shiftKey && marks.inMs !== null ? marks.inMs : 0);
      else if (k === 'End') seek(e.shiftKey && marks.outMs !== null ? marks.outMs : (durationMs ?? msOf(video.current)));
      else return false;
      e.preventDefault();
      return true;
    },
    [source, togglePlay, setShuttle, markIn, markOut, step, seek, marks, onMarks, durationMs],
  );

  useImperativeHandle(ref, () => ({ togglePlay, playRange, seek, handleKey }), [togglePlay, playRange, seek, handleKey]);

  // a new asset: stop, back to its start (or its in mark)
  useEffect(() => {
    stopReverse();
    stopAt.current = null;
    shuttle.current = 0;
    const h = window.setTimeout(() => {
      setRate(0);
      setPlaying(false);
      setTMs(0);
    }, 0);
    return () => window.clearTimeout(h);
  }, [source?.key, source?.url]);

  // the clock, a frame at a time while playing
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const v = el();
      if (v && !v.paused) {
        const ms = Math.round(v.currentTime * 1000);
        setTMs(ms);
        if (stopAt.current !== null && ms >= stopAt.current) {
          if (loop) v.currentTime = (marks.inMs ?? 0) / 1000;
          else {
            v.pause();
            v.currentTime = stopAt.current / 1000;
            stopAt.current = null;
          }
        } else if (loop && marks.outMs !== null && ms >= marks.outMs) v.currentTime = (marks.inMs ?? 0) / 1000;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [loop, marks.inMs, marks.outMs]);

  useEffect(
    () => () => {
      stopReverse();
      levels.close();
    },
    [levels],
  );

  // ── the trackbar: click or drag to seek ──
  const msAt = (clientX: number) => {
    const r = bar.current?.getBoundingClientRect();
    if (!r || !durationMs) return 0;
    return Math.round(Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * durationMs);
  };
  const onBarDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!durationMs) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    seek(msAt(e.clientX));
  };
  const onBarMove = (e: React.PointerEvent<HTMLDivElement>) => {
    setHover(msAt(e.clientX));
    if (e.buttons === 1) seek(msAt(e.clientX));
  };
  const pct = (ms: number | null) => (durationMs && ms !== null ? `${Math.min(100, Math.max(0, (ms / durationMs) * 100))}%` : '0%');
  const selLen = marks.inMs !== null || marks.outMs !== null ? (marks.outMs ?? durationMs ?? 0) - (marks.inMs ?? 0) : null;
  const commitTc = (which: 'in' | 'out', text: string) => {
    const ms = text.trim() === '' ? null : parseFrameTc(text);
    if (ms === undefined || (text.trim() !== '' && ms === null)) return;
    const next = { ...marks, [which === 'in' ? 'inMs' : 'outMs']: ms === null ? null : Math.min(ms, durationMs ?? ms) };
    if (next.inMs !== null && next.outMs !== null && next.outMs <= next.inMs) return;
    onMarks(next);
  };

  return (
    <div className="mamp">
      <div className="mamp-screen">
        <div className={`mamp-picture${source?.media === 'audio' ? ' audio' : ''}`}>
          <video
            ref={video}
            src={source?.url}
            poster={source?.media === 'video' ? (source.poster ?? undefined) : undefined}
            preload="auto"
            playsInline
            muted={muted}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onLoadedMetadata={(e) => source && Number.isFinite(e.currentTarget.duration) && setMetaMs({ key: source.key, ms: Math.round(e.currentTarget.duration * 1000) })}
            onError={() => source && setFailed(source.key)}
            onLoadStart={() => setFailed(null)}
            onClick={togglePlay}
          />
          {source?.media === 'audio' && (
            <div className="mamp-wave" onClick={togglePlay}>
              {source.wave ? <img alt="" src={source.wave} /> : <span className="mamp-wave-none">♪</span>}
              <span className="mamp-wave-head" style={{ left: pct(tMs) }} />
            </div>
          )}
          {!source && <div className="mamp-empty">{t('mamPlayerEmpty')}</div>}
          {error && <div className="mamp-empty">{t('cueNoPreview')}</div>}
          {source && (
            <div className="mamp-osd">
              <span className="mamp-osd-title">{source.title}</span>
              {rate !== 0 && rate !== 1 && <span className="mamp-osd-rate">{rate > 0 ? `▶ ×${rate}` : `◀ ×${-rate}`}</span>}
            </div>
          )}
        </div>
        <VuMeter levels={levels} reset={source?.key} />
      </div>

      <div className="mamp-times">
        <span className="mamp-tc now">{frameTc(tMs)}</span>
        <span className="mamp-sel">
          {selLen !== null && (
            <>
              {t('mamSelection')} <b>{frameTc(Math.max(0, selLen))}</b>
            </>
          )}
        </span>
        <span className="mamp-tc">{durationMs !== null ? frameTc(durationMs) : '--:--:--:--'}</span>
      </div>
      <div ref={bar} className="mamp-bar" onPointerDown={onBarDown} onPointerMove={onBarMove} onPointerLeave={() => setHover(null)}>
        <div className="mamp-bar-track" />
        {subclips.map((s) => (
          <div key={s.id} className="mamp-bar-sub" style={{ left: pct(s.inMs), width: `calc(${pct(s.outMs)} - ${pct(s.inMs)})` }} title={s.name} />
        ))}
        {(marks.inMs !== null || marks.outMs !== null) && <div className="mamp-bar-sel" style={{ left: pct(marks.inMs ?? 0), width: `calc(${pct(marks.outMs ?? durationMs)} - ${pct(marks.inMs ?? 0)})` }} />}
        {marks.inMs !== null && <div className="mamp-bar-mark in" style={{ left: pct(marks.inMs) }} />}
        {marks.outMs !== null && <div className="mamp-bar-mark out" style={{ left: pct(marks.outMs) }} />}
        <div className="mamp-bar-done" style={{ width: pct(tMs) }} />
        <div className="mamp-bar-knob" style={{ left: pct(tMs) }} />
        {hover !== null && durationMs ? (
          <div className="mamp-bar-hover" style={{ left: pct(hover) }}>
            {frameTc(hover)}
          </div>
        ) : null}
      </div>

      <div className="mamp-transport">
        <div className="mamp-markbox">
          <button type="button" className="mamp-btn" disabled={!source || marks.inMs === null} onClick={() => onMarks({ ...marks, inMs: null })} title={`${t('mamClearIn')} (Alt+I)`}>
            ⊘
          </button>
          <button type="button" className="mamp-btn" disabled={!source} onClick={markIn} title={`${t('mamMarkIn')} (I)`}>
            [
          </button>
          <input className="mamp-tcin" value={inText ?? (marks.inMs !== null ? frameTc(marks.inMs) : '')} placeholder="IN" onChange={(e) => setInText(e.target.value)} onFocus={() => setInText(marks.inMs !== null ? frameTc(marks.inMs) : '')} onBlur={(e) => { commitTc('in', e.target.value); setInText(null); }} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} disabled={!source} />
        </div>
        <div className="mamp-deck">
          <button type="button" className="mamp-btn" disabled={!source} onClick={() => seek(0)} title={`${t('mamToStart')} (Home)`}>
            ⏮
          </button>
          <button type="button" className="mamp-btn" disabled={!source || marks.inMs === null} onClick={() => seek(marks.inMs ?? 0)} title={`${t('mamToIn')} (Shift+Home)`}>
            ⇤
          </button>
          <button type="button" className="mamp-btn" disabled={!source} onClick={() => step(-5)} title={`${t('mamBack5')} (Shift+←)`}>
            «
          </button>
          <button type="button" className="mamp-btn" disabled={!source} onClick={() => step(-1)} title={`${t('mamBack1')} (←)`}>
            ‹
          </button>
          <button type="button" className={`mamp-btn play${playing ? ' on' : ''}`} disabled={!source} onClick={togglePlay} title={`${playing ? t('vePause') : t('vePlay')} (space · J K L)`}>
            {playing ? '❚❚' : '▶'}
          </button>
          <button type="button" className="mamp-btn" disabled={!source} onClick={() => step(1)} title={`${t('mamFwd1')} (→)`}>
            ›
          </button>
          <button type="button" className="mamp-btn" disabled={!source} onClick={() => step(5)} title={`${t('mamFwd5')} (Shift+→)`}>
            »
          </button>
          <button type="button" className="mamp-btn" disabled={!source || marks.outMs === null} onClick={() => seek(marks.outMs ?? 0)} title={`${t('mamToOut')} (Shift+End)`}>
            ⇥
          </button>
          <button type="button" className="mamp-btn" disabled={!source} onClick={() => seek(durationMs ?? 0)} title={`${t('mamToEnd')} (End)`}>
            ⏭
          </button>
        </div>
        <div className="mamp-markbox">
          <input className="mamp-tcin" value={outText ?? (marks.outMs !== null ? frameTc(marks.outMs) : '')} placeholder="OUT" onChange={(e) => setOutText(e.target.value)} onFocus={() => setOutText(marks.outMs !== null ? frameTc(marks.outMs) : '')} onBlur={(e) => { commitTc('out', e.target.value); setOutText(null); }} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} disabled={!source} />
          <button type="button" className="mamp-btn" disabled={!source} onClick={markOut} title={`${t('mamMarkOut')} (O)`}>
            ]
          </button>
          <button type="button" className="mamp-btn" disabled={!source || marks.outMs === null} onClick={() => onMarks({ ...marks, outMs: null })} title={`${t('mamClearOut')} (Alt+O)`}>
            ⊘
          </button>
        </div>
      </div>
      <div className="mamp-extra">
        <button type="button" className="mamp-btn wide" disabled={!source || (marks.inMs === null && marks.outMs === null)} onClick={() => playRange()} title={t('mamPlayRange')}>
          ▶[ ]
        </button>
        <button type="button" className={`mamp-btn${loop ? ' on' : ''}`} disabled={!source} onClick={() => setLoop((x) => !x)} title={t('mamLoop')}>
          ⟲
        </button>
        <button type="button" className={`mamp-btn${muted ? ' on' : ''}`} disabled={!source} onClick={() => setMuted((x) => !x)} title={t('mamMute')}>
          {muted ? '🔇' : '🔊'}
        </button>
        <span className="mamp-spacer" />
        {actions}
      </div>
    </div>
  );
});
